import { Component, DoCheck, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonDirective, ButtonModule } from 'primeng/button';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import {
  ClientManagementService,
  CustomerManagementService,
  ProductManagementService,
  ProjectManagementService,
  WorkerManagementService,
  UserManagementService,
  WorkerTimeManagementService,
  PayrollManagementService,
  PayrollPaymentManagementService,
  PayrollSettlementManagementService,
  StockImportManagementService,
} from '@elesgergo/ikont-web-client-sdk';
import { extractImportErrorPayload } from 'src/app/shared/service/upload.service';
import { BYPASS_ERROR_INTERCEPTOR } from 'src/app/core/interceptors/bypass-error-interceptor.token';

export interface WrongTemplateDialogData {
  wrongTemplate: { key: string; label: string };
  blob: Blob;
}

/**
 * A backend akkor dob `wrongTemplate`-tel ellátott hibát, ha a feltöltött
 * fájl fejléce nem az aktuális tábla, hanem egy MÁSIK tábla sablonjának
 * felel meg (ld. `generic-excel-import.util.ts` / `import-templates.registry.ts`
 * a backend oldalon). Ez a dialógus felajánlja, hogy ugyanazt a fájlt
 * egy kattintással a helyes táblához töltsük fel, navigáció és a fájl
 * újbóli kiválasztása nélkül.
 */
@Component({
  selector: 'ik-wrong-template-dialog',
  templateUrl: './wrong-template-dialog.component.html',
  styleUrls: ['./wrong-template-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonDirective, ButtonModule],
})
export class WrongTemplateDialogComponent implements DoCheck {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  toastr = inject(ToastrService);

  private clientService = inject(ClientManagementService);
  private customerService = inject(CustomerManagementService);
  private productService = inject(ProductManagementService);
  private projectService = inject(ProjectManagementService);
  private workerService = inject(WorkerManagementService);
  private userService = inject(UserManagementService);
  private workerTimeService = inject(WorkerTimeManagementService);
  private payrollService = inject(PayrollManagementService);
  private payrollPaymentService = inject(PayrollPaymentManagementService);
  private payrollSettlementService = inject(PayrollSettlementManagementService);
  private stockImportService = inject(StockImportManagementService);

  uploading = false;

  /**
   * Ugyanaz a minta, mint `UploadComponent`-nél: a PrimeNG DynamicDialog a
   * `closable`/`closeOnEscape`/`dismissableMask` mezőket élőben, minden
   * change detection körben újraolvassa a `config`-ból, ezért feltöltés
   * közben itt is letiltjuk (X gomb, ESC, háttérre kattintás), utána
   * pedig visszaengedjük.
   */
  ngDoCheck(): void {
    const closable = !this.uploading;
    this.config.closable = closable;
    this.config.closeOnEscape = closable;
    this.config.dismissableMask = closable;
  }

  get wrongTemplate(): { key: string; label: string } {
    return (this.config.data as WrongTemplateDialogData).wrongTemplate;
  }

  /**
   * A globális `ErrorInterceptor` minden HTTP hibára megjelenít egy saját,
   * címzett toastot is - ez itt duplán mutatná a hibát a lenti `subscribe`
   * `error` ágának saját toastja mellett. A `BYPASS_ERROR_INTERCEPTOR`
   * kontextussal kikapcsoljuk az interceptor automatikus toastját erre a
   * konkrét kérésre (ld. `camera-details.component.ts` ugyanezt a mintát).
   */
  private readonly bypassErrorInterceptorContext = new HttpContext().set(
    BYPASS_ERROR_INTERCEPTOR,
    true
  );

  /** Táblakulcs -> a megfelelő SDK import hívás. */
  private uploadByKey(key: string, blob: Blob): Observable<any> {
    const options = { context: this.bypassErrorInterceptorContext };
    switch (key) {
      case 'client':
        return this.clientService.clientManagementControllerUploadClientsV0(
          blob,
          'body',
          false,
          options
        );
      case 'customer':
        return this.customerService.customerManagementControllerUploadCustomersV0(
          blob,
          'body',
          false,
          options
        );
      case 'product':
        return this.productService.productManagementControllerImportV0(
          blob,
          'body',
          false,
          options
        );
      case 'project':
        return this.projectService.projectManagementControllerImportFileV0(
          blob,
          'body',
          false,
          options
        );
      case 'worker':
        return this.workerService.workerManagementControllerImportV0(
          blob,
          'body',
          false,
          options
        );
      case 'user':
        return this.userService.userManagementControllerImportV0(
          blob,
          'body',
          false,
          options
        );
      case 'workerTime':
        return this.workerTimeService.workerTimeManagementControllerImportV0(
          blob,
          'body',
          false,
          options
        );
      case 'payroll':
        return this.payrollService.payrollManagementControllerImportV0(
          blob,
          'body',
          false,
          options
        );
      case 'payrollPayment':
        return this.payrollPaymentService.payrollPaymentManagementControllerUploadFromFileV0(
          blob,
          'body',
          false,
          options
        );
      case 'payrollSettlement':
        return this.payrollSettlementService.payrollSettlementManagementControllerUploadEntitiesV0(
          blob,
          'body',
          false,
          options
        );
      case 'stockImport':
        return this.stockImportService.stockImportManagementControllerUploadStockImportsV0(
          blob,
          'body',
          false,
          options
        );
      default:
        throw new Error(`Ismeretlen tábla: ${key}`);
    }
  }

  uploadToCorrectTable(): void {
    const { wrongTemplate, blob } = this.config.data as WrongTemplateDialogData;
    this.uploading = true;

    let request: Observable<any>;
    try {
      request = this.uploadByKey(wrongTemplate.key, blob);
    } catch (error) {
      this.uploading = false;
      this.toastr.error((error as Error).message);
      return;
    }

    request.subscribe({
      next: () => {
        this.toastr.success(`Sikeresen feltöltve a(z) "${wrongTemplate.label}" táblához!`);
        this.ref.close();
      },
      error: (error: unknown) => {
        this.uploading = false;
        // A BYPASS_ERROR_INTERCEPTOR miatt itt a nyers HttpErrorResponse
        // érkezik (nem az interceptor által már kicsomagolt body), ezért a
        // `.error` mezőt adjuk át - az hordozza a backend válasz törzsét.
        const responseBody = (error as HttpErrorResponse)?.error;
        this.toastr.error(
          extractImportErrorPayload(responseBody).message || 'A feltöltés sikertelen volt.'
        );
      },
    });
  }
}
